/**
 * TimePicker12 — 12-hour AM/PM time picker.
 *
 * Stores value as a 24-hour "HH:MM" string under the hood (so the backend
 * contract stays the same), but presents Hour (1–12) + Minute + AM/PM toggle
 * to the user.
 *
 * Props:
 *   value      — "HH:MM" 24-hour string (e.g. "17:30"), or "" for empty
 *   onChange   — (next: "HH:MM") => void
 *   minuteStep — granularity in minutes (default 30)
 *   testid
 *   disabled
 */
import { useMemo } from "react";

function to24h({ hour12, minute, period }) {
    let h = parseInt(hour12, 10);
    if (Number.isNaN(h)) h = 12;
    if (period === "AM") {
        if (h === 12) h = 0;
    } else {
        if (h !== 12) h += 12;
    }
    const mm = String(minute).padStart(2, "0");
    return `${String(h).padStart(2, "0")}:${mm}`;
}

function from24h(v) {
    if (!v || !/^\d{1,2}:\d{2}/.test(v)) return { hour12: "12", minute: "00", period: "AM" };
    const [h, m] = v.split(":").map(x => parseInt(x, 10));
    const period = h >= 12 ? "PM" : "AM";
    let hour12 = h % 12;
    if (hour12 === 0) hour12 = 12;
    return { hour12: String(hour12), minute: String(m).padStart(2, "0"), period };
}

export default function TimePicker12({ value, onChange, minuteStep = 30, testid = "time-picker", disabled = false }) {
    const parsed = useMemo(() => from24h(value), [value]);
    const minuteOptions = useMemo(() => {
        const arr = [];
        for (let m = 0; m < 60; m += minuteStep) arr.push(String(m).padStart(2, "0"));
        return arr;
    }, [minuteStep]);

    const update = (patch) => {
        const next = { ...parsed, ...patch };
        onChange?.(to24h(next));
    };

    const selectCls = "h-10 px-1.5 bg-slate-950 border border-slate-700 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#00FF66] disabled:opacity-50 min-w-0";

    return (
        <div className="flex flex-wrap items-center gap-1" data-testid={testid}>
            <select
                value={parsed.hour12}
                onChange={(e) => update({ hour12: e.target.value })}
                disabled={disabled}
                data-testid={`${testid}-hour`}
                className={selectCls}
                aria-label="Hour"
            >
                {Array.from({ length: 12 }, (_, i) => String(i + 1)).map(h => (
                    <option key={h} value={h}>{h}</option>
                ))}
            </select>
            <span className="text-slate-500 text-sm">:</span>
            <select
                value={parsed.minute}
                onChange={(e) => update({ minute: e.target.value })}
                disabled={disabled}
                data-testid={`${testid}-minute`}
                className={selectCls}
                aria-label="Minute"
            >
                {minuteOptions.map(m => (
                    <option key={m} value={m}>{m}</option>
                ))}
            </select>
            <div className="inline-flex rounded border border-slate-700 overflow-hidden" role="group" aria-label="AM/PM">
                {["AM", "PM"].map(p => (
                    <button
                        key={p}
                        type="button"
                        onClick={() => update({ period: p })}
                        disabled={disabled}
                        data-testid={`${testid}-${p.toLowerCase()}`}
                        className={`h-10 px-2 text-[11px] font-bold tracking-wider transition ${
                            parsed.period === p
                                ? "bg-[#00FF66] text-black"
                                : "bg-slate-950 text-slate-400 hover:text-white"
                        } disabled:opacity-50`}
                    >
                        {p}
                    </button>
                ))}
            </div>
        </div>
    );
}

/** Format a 24-hour HH:MM string to 12-hour display ("5:30 PM"). */
export function fmt12(value) {
    if (!value || !/^\d{1,2}:\d{2}/.test(value)) return value || "";
    const [h, m] = value.split(":").map(x => parseInt(x, 10));
    const period = h >= 12 ? "PM" : "AM";
    let hour12 = h % 12; if (hour12 === 0) hour12 = 12;
    return `${hour12}:${String(m).padStart(2, "0")} ${period}`;
}
